import { useState } from "react";
import { Button } from "@/components/ui/button";
import { QnaQuestionsList } from "./QnaQuestionsList";
import { qnaActionStyle } from "./qnaStyles";
import { DEMO_QNA_QUESTIONS, type DemoQnaQuestion } from "./demoQnaQuestions";
import { DemoQnaUploadDialog } from "./DemoQnaUploadDialog";
import { DemoQnaDealImpact } from "./DemoQnaDealImpact";
import { toast } from "@/hooks/use-toast";
import { useDemoQnaAiCheck } from "./useDemoQnaAiCheck";

export function DemoQnaStage({ onBack }: { onBack: () => void }) {
  const [questions, setQuestions] = useState<DemoQnaQuestion[]>(() => DEMO_QNA_QUESTIONS.map((question) => ({
    ...question,
    answer: null,
    soWhat: null,
    reviewed: false,
    follow_up: null,
    deal_implication: null,
  })));
  const [responsesUploaded, setResponsesUploaded] = useState(false);
  function applyAnswers(answers: { id: string; answer: string }[], restoreDemoChecks = false) {
    setQuestions((previous) =>
      previous.map((question) => {
        const match = answers.find((answer) => answer.id === question.id);
        const example = restoreDemoChecks
          ? DEMO_QNA_QUESTIONS.find((item) => item.id === question.id)
          : undefined;
        return match
          ? {
              ...question,
              answer: match.answer,
              revision: question.revision + 1,
              soWhat: restoreDemoChecks ? question.soWhat ?? example?.soWhat ?? "clear" : null,
              follow_up: restoreDemoChecks ? question.follow_up || example?.follow_up || null : null,
              reviewed: restoreDemoChecks ? question.reviewed || example?.reviewed || false : false,
              deal_implication: restoreDemoChecks ? question.deal_implication || example?.deal_implication || null : null,
            }
          : question;
      }),
    );
    toast({ title: "Demo answers applied" });
  }
  const aiCheck = useDemoQnaAiCheck(questions, (update) => {
    setQuestions(previous => {
      const outcomes = update(Object.fromEntries(previous.map(q => [q.id, q.soWhat ?? null])));
      return previous.map(q => ({ ...q, soWhat: outcomes[q.id] }));
    });
  }, (id, text) => {
    setQuestions(previous => previous.map(q => q.id === id ? { ...q, follow_up: text } : q));
  });
  return (
    <section className="min-h-0 flex-1 space-y-5 overflow-auto p-8">
      <p className="text-sm text-muted-foreground">
        Demo project. Uploads preview example matches; downloads provide sample
        files.
      </p>
      <QnaQuestionsList
        questions={questions}
        getOutcome={aiCheck.getOutcome}
        renderAiCheck={aiCheck.renderAiCheck}
        onOpenQuestion={aiCheck.openQuestion}
        canEdit
        busy={false}
        summary={responsesUploaded ? <DemoQnaDealImpact questions={questions} /> : undefined}
        actions={
          <>
            <DemoQnaUploadDialog
              kind="notes"
              questions={questions}
              onApply={applyAnswers}
            />
            <a
              href="/Project_Beta_QA_v2.xlsx"
              download="Project_Beta_QA_v2.xlsx"
              title="Download sample Q&A"
              style={qnaActionStyle}
            >
              <svg width="16" height="16" viewBox="0 0 16 16">
                <rect
                  x="1"
                  y="1"
                  width="14"
                  height="14"
                  rx="2"
                  fill="#217346"
                />
                <text
                  x="8"
                  y="11.5"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill="#fff"
                >
                  X
                </text>
              </svg>
              Download Q&A
            </a>
          </>
        }
        responseActions={
          <>
            <DemoQnaUploadDialog
              kind="responses"
              questions={questions}
              onApply={(answers) => {
                applyAnswers(answers, true);
                setResponsesUploaded(true);
              }}
            />
            <a
              href="/QA_Meeting_Agenda.pptx"
              download="Q&A Meeting Agenda.pptx"
              title="Download sample meeting agenda"
              style={qnaActionStyle}
            >
              <svg width="16" height="16" viewBox="0 0 16 16">
                <rect
                  x="1"
                  y="1"
                  width="14"
                  height="14"
                  rx="2"
                  fill="#D24726"
                />
                <text
                  x="8"
                  y="11.5"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill="#fff"
                >
                  P
                </text>
              </svg>
              Make an agenda
            </a>
          </>
        }
        onAdd={async (question) => {
          setQuestions((previous) => [
            ...previous,
            {
              ...question,
              id: crypto.randomUUID(),
              revision: 1,
              is_custom: true,
              status: "Open",
              priority: question.priority ?? "Medium",
              period: question.period ?? "—",
              value: null,
              variance: null,
              answer: question.answer ?? null,
              reviewed: false,
              follow_up: null,
              deal_implication: null,
            },
          ]);
        }}
        onUpdate={async (question, update) => {
          setQuestions((previous) =>
            previous.map((item) =>
              item.id === question.id
                ? {
                    ...item,
                    ...update,
                    revision: item.revision + 1,
                    ...("answer" in update || "question" in update
                      ? {
                          soWhat: null,
                          follow_up: null,
                          reviewed: false,
                          deal_implication: null,
                        }
                      : {}),
                  }
                : item,
            ),
          );
        }}
      />
      {aiCheck.panel}
      <Button variant="outline" onClick={onBack}>
        ← Databook
      </Button>
    </section>
  );
}
